import { Link, useParams, useSearchParams } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page, usePoll, Empty, Chip, Stat } from './ui'

/**
 * Leaderboards, chosen one sport at a time.
 *
 * "Most sixes" means nothing next to "most goals", so there is no combined
 * table: pick the sport first, then narrow by city and draw. The tables that
 * appear are the ones that sport actually keeps.
 */
const Leaderboards = () => {
  const { sport } = useParams()

  return sport ? <SportBoards sport={sport} /> : <SportMenu />
}

const SportMenu = () => {
  const { data, error } = usePoll(() => reportingApi.statSports(), 0, [])

  const groups = [['outdoor', 'Outdoor'], ['indoor', 'Indoor']]

  return (
    <div style={page}>
      <h2 style={{ margin: '0 0 4px', fontSize: 24 }}>Leaderboards</h2>
      <p style={{ margin: '0 0 22px', color: T.muted, fontSize: 14, maxWidth: 620 }}>
        Each sport keeps its own records. Pick one to see its tables, then filter by
        city or draw.
      </p>

      {error && <Empty>{error}</Empty>}
      {!data && !error && <Empty>Loading sports…</Empty>}

      {groups.map(([setting, label]) => {
        const sports = (data?.data || []).filter((s) => s.setting === setting)
        if (sports.length === 0) return null

        return (
          <section key={setting} style={{ marginBottom: 28 }}>
            <h3 style={{
              fontSize: 11.5, letterSpacing: '.12em', textTransform: 'uppercase',
              color: T.faint, fontWeight: 800, margin: '0 0 10px',
            }}>{label}</h3>

            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            }}>
              {sports.map((s) => (
                <Link key={s.sport} to={`/board/leaderboards/${s.sport}`} style={{
                  display: 'block', background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: 12, padding: '16px 18px', textDecoration: 'none', color: T.ink,
                }}>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{s.label}</div>
                  <div style={{
                    marginTop: 8, fontSize: 12.5, color: T.muted, fontVariantNumeric: 'tabular-nums',
                  }}>
                    {s.played.toLocaleString('en-IN')} played · {s.cities} {s.cities === 1 ? 'city' : 'cities'}
                  </div>
                  {s.live > 0 && (
                    <div style={{ marginTop: 6, fontSize: 12, color: T.live, fontWeight: 700 }}>
                      {s.live} live now
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )
      })}
    </div>
  )
}

const SportBoards = ({ sport }) => {
  const [params, setParams] = useSearchParams()
  const city = params.get('city') || ''
  const category = params.get('category') || ''

  const { data, error } = usePoll(
    () => reportingApi.stats({ sport, city: city || undefined, category: category || undefined }),
    0,
    [sport, city, category],
  )

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params)
    value ? next.set(key, value) : next.delete(key)
    setParams(next)
  }

  if (error) return <div style={page}><Empty>{error}</Empty></div>
  if (!data) return <div style={page}><Empty>Loading leaderboards…</Empty></div>

  const boards = Object.entries(data.boards || {})

  return (
    <div style={page}>
      <Link to="/board/leaderboards" style={back}>← All sports</Link>

      <h2 style={{ margin: '8px 0 4px', fontSize: 26 }}>{data.sport_label}</h2>
      <p style={{ margin: '0 0 18px', color: T.muted, fontSize: 13.5 }}>
        {[city || 'Every city', category || 'Every draw'].join(' · ')}
      </p>

      <div style={{ display: 'grid', gap: 10, marginBottom: 20 }}>
        <FilterRow label="City">
          <Chip on={!city} onClick={() => setFilter('city', '')}>All</Chip>
          {(data.filters?.cities || []).map((c) => (
            <Chip key={c} on={city === c} onClick={() => setFilter('city', c)}>{c}</Chip>
          ))}
        </FilterRow>

        {(data.filters?.categories || []).length > 1 && (
          <FilterRow label="Draw">
            <Chip on={!category} onClick={() => setFilter('category', '')}>All</Chip>
            {data.filters.categories.map((c) => (
              <Chip key={c} on={category === c} onClick={() => setFilter('category', c)}>{c}</Chip>
            ))}
          </FilterRow>
        )}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
        gap: 12, marginBottom: 24,
      }}>
        <Stat value={data.overview.played.toLocaleString('en-IN')} label="Played" />
        <Stat value={data.overview.live} label="Live now" tone="live" />
        <Stat value={data.overview.remaining.toLocaleString('en-IN')} label="To play" />
        <Stat value={data.overview.matches.toLocaleString('en-IN')} label="Total" />
      </div>

      {data.overview.played === 0 && (
        <Empty>No matches decided under these filters yet, so the tables are empty.</Empty>
      )}

      <div style={{
        display: 'grid', gap: 14,
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      }}>
        {boards.map(([key, board]) => (
          <Board key={key} board={board} />
        ))}
      </div>
    </div>
  )
}

const Board = ({ board }) => {
  const rows = board.rows || []
  const top = rows[0]?.value || 1

  return (
    <section style={{
      background: T.card, border: `1px solid ${T.line}`, borderRadius: 12, padding: '16px 18px',
    }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        marginBottom: 12, gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 15.5 }}>{board.title}</h3>
        <span style={{
          fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase',
          color: T.faint, fontWeight: 800,
        }}>{board.unit}</span>
      </div>

      {rows.length === 0 && <Empty>Nothing to rank yet.</Empty>}

      {rows.map((r, i) => (
        <div key={`${r.name}-${i}`} style={{
          display: 'grid', gridTemplateColumns: '20px 1fr auto', gap: 10,
          alignItems: 'center', padding: '7px 0',
          borderTop: i === 0 ? 'none' : `1px solid ${T.line}`,
        }}>
          <span style={{
            fontSize: 12, fontWeight: 800, color: i < 3 ? T.accent : T.faint,
            fontVariantNumeric: 'tabular-nums',
          }}>{i + 1}</span>

          <div style={{ minWidth: 0 }}>
            <div style={{
              fontSize: 13.5, fontWeight: 600, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{r.name}</div>
            {r.detail && (
              <div style={{ fontSize: 11.5, color: T.faint, marginTop: 1 }}>{r.detail}</div>
            )}
            <div style={{
              marginTop: 4, height: 3, borderRadius: 2, background: '#eef1f5', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.round((r.value / top) * 100)}%`, height: '100%',
                background: i < 3 ? T.accent : '#c7d0da',
              }} />
            </div>
          </div>

          <span style={{
            fontSize: 17, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
          }}>{r.value}</span>
        </div>
      ))}
    </section>
  )
}

const FilterRow = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
    <span style={{
      fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase',
      color: T.faint, fontWeight: 800, minWidth: 42,
    }}>{label}</span>
    {children}
  </div>
)

const back = { fontSize: 13, color: T.muted, textDecoration: 'none', fontWeight: 600 }

export default Leaderboards
